/**
 * Core type definitions for the filter system
 *
 * This module contains all the TypeScript types and interfaces that power
 * the type-safe filter system. These types ensure compile-time safety when
 * building Prisma where clauses from user-defined filters.
 *
 * @module types
 */

/**
 * Base filter definition
 *
 * Represents a single active filter in the system.
 *
 * @property id - Unique identifier for this filter instance
 * @property filterId - References the FieldConfig.id to identify which field
 * @property operator - The comparison operator to use
 * @property value - The value(s) to filter by
 *
 * @example
 * ```typescript
 * const activeFilter: Filter = {
 *   id: 'filter_1234567890_abc',
 *   filterId: 'user-name',
 *   operator: 'contains',
 *   value: 'john'
 * };
 * ```
 */
export interface Filter {
    id: string;
    filterId: string; // References FieldConfig.id
    operator: FilterOperator;
    value: FilterValue;
}

/**
 * All possible filter operators
 *
 * These map directly to Prisma's query operators with some additions
 * for better UX (e.g., 'between' becomes gte + lte).
 *
 * @example
 * ```typescript
 * // Text operators
 * 'equals' | 'not' | 'contains' | 'startsWith' | 'endsWith'
 *
 * // Numeric/Date operators
 * 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'notBetween'
 *
 * // Array operators
 * 'in' | 'notIn'
 *
 * // Null operators
 * 'isNull' | 'isNotNull'
 * ```
 */
export type FilterOperator =
    | 'equals'
    | 'not'
    | 'in'
    | 'notIn'
    | 'contains'
    | 'startsWith'
    | 'endsWith'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'isNull'
    | 'isNotNull'
    | 'between'
    | 'notBetween'
    | 'withinRelative';

/**
 * Field types that determine available operators and UI
 *
 * Each field type has:
 * - Specific set of allowed operators
 * - Appropriate UI control (text input, date picker, etc.)
 * - Value transformation and validation
 *
 * @example
 * ```typescript
 * // Text field: renders Input, allows text operators
 * type: 'text'
 *
 * // Number field: renders InputNumber, allows numeric operators
 * type: 'number'
 *
 * // Multiselect: renders Select with multiple, only allows in/notIn
 * type: 'multiselect'
 * ```
 */
export type FieldType =
    | 'text'
    | 'textSearch'
    | 'number'
    | 'bigint'
    | 'date'
    | 'datetime'
    | 'boolean'
    | 'select'
    | 'multiselect'
    | 'json';

/**
 * Extract all valid keys from a Prisma where input type
 */
export type PrismaWhereKeys<T> = {
    [K in keyof T]-?: K extends 'AND' | 'OR' | 'NOT' ? never : K;
}[keyof T];

/**
 * Utility type to create a strict partial of TWhere that only allows valid keys
 */
export type StrictPartial<TWhere> = {
    [K in keyof TWhere]?: TWhere[K];
};

/**
 * Type that ensures the returned where clause conforms to TWhere structure
 */
export type ValidatedWhereClause<TWhere> = {
    [K in keyof TWhere]?: TWhere[K];
} & {
    // This ensures that any additional properties will cause a type error
    [key: string]: unknown;
};

/**
 * Represents a valid Prisma where clause including logical operators.
 * Every Prisma WhereInput has AND, OR, NOT at the top level.
 * This type allows where builders to return logical combinations
 * without needing `as any` casts.
 */
export type PrismaWhereClause<TWhere> =
    | Partial<TWhere>
    | { AND: PrismaWhereClause<TWhere>[] }
    | { OR: PrismaWhereClause<TWhere>[] }
    | { NOT: PrismaWhereClause<TWhere> | PrismaWhereClause<TWhere>[] };

/**
 * Union of all possible filter value types.
 * Narrower than `any` but broad enough for all field types.
 */
export type FilterValue =
    | string
    | number
    | bigint
    | boolean
    | Date
    | null
    | string[]
    | number[]
    | bigint[]
    | Date[]
    | (bigint | null)[];

/**
 * Map field types to their allowed operators
 */
export type OperatorsByFieldType<T extends FieldType> = T extends 'text'
    ? 'equals' | 'not' | 'contains' | 'startsWith' | 'endsWith' | 'isNull' | 'isNotNull'
    : T extends 'textSearch'
      ? 'equals' | 'not' | 'contains' | 'startsWith' | 'endsWith' | 'isNull' | 'isNotNull'
      : T extends 'number'
        ? 'equals' | 'not' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'isNull' | 'isNotNull'
        : T extends 'bigint'
          ? 'equals' | 'not' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'isNull' | 'isNotNull'
          : T extends 'date'
            ? 'equals' | 'not' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'withinRelative' | 'isNull' | 'isNotNull'
            : T extends 'datetime'
              ? 'equals' | 'not' | 'gt' | 'gte' | 'lt' | 'lte' | 'between' | 'withinRelative' | 'isNull' | 'isNotNull'
              : T extends 'boolean'
                ? 'equals' | 'not' | 'isNull' | 'isNotNull'
                : T extends 'select'
                  ? 'equals' | 'not' | 'in' | 'notIn' | 'isNull' | 'isNotNull'
                  : T extends 'multiselect'
                    ? 'in' | 'notIn' | 'isNull' | 'isNotNull'
                    : T extends 'json'
                      ? 'equals' | 'not' | 'contains' | 'isNull' | 'isNotNull'
                      : FilterOperator;

/**
 * Type-safe custom where builder function
 */
export type CustomWhereBuilder<TWhere, TValue = FilterValue> = (
    operator: FilterOperator,
    value: TValue,
    fieldName: string,
) => PrismaWhereClause<TWhere> | null;

/**
 * Type-safe custom where builder with field type
 */
export type TypedCustomWhereBuilder<TWhere, TValue = FilterValue, TFieldType extends FieldType = FieldType> = {
    /**
     * Bivariant function type to allow assigning narrower operator parameter types
     * when callers specify a restricted operators list.
     */
    bivarianceHack(
        operator: OperatorsByFieldType<TFieldType>,
        value: TValue,
        fieldName: string,
    ): PrismaWhereClause<TWhere> | null;
}['bivarianceHack'];

/**
 * Configuration for a single field
 */
export interface FieldConfig<TValue = any, TWhere = any> {
    /**
     * Unique identifier for this filter
     */
    id: string;
    label: string;
    type?: FieldType;
    operators?: FilterOperator[];
    options?:
        | Array<{ label: string; value: TValue }>
        | (() => Promise<Array<{ label: string; value: TValue }>>)
        | ((whereClause?: TWhere) => Promise<Array<{ label: string; value: TValue }>>);
    transform?: (value: unknown) => TValue | null | undefined;
    format?: (value: TValue) => string;
    renderValue?: React.ComponentType<{
        value: any;
        onChange: (value: any) => void;
        operator?: FilterOperator;
        autoFocus?: boolean;
        whereClause?: TWhere;
        options?: Array<{ label: string; value: TValue }>;
        getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    }>;
    placeholder?: string;
    multiple?: boolean;
    defaultOperator?: FilterOperator;
    defaultValue?: any;
    /**
     * Custom where clause builder for complex field conditions
     * Takes precedence over default where building logic
     */
    customWhereBuilder?: CustomWhereBuilder<TWhere, TValue>;
    /**
     * Optional array of filter IDs that trigger options re-fetching
     * When specified, options will only reload when these specific filters change
     * If not specified, options will reload on any whereClause change
     */
    optionsDependsOn?: string[];
    /**
     * When true, this filter field is always visible in the filter bar
     * Pinned fields cannot be removed by the user
     */
    pinned?: boolean;
}

/**
 * Type-safe field configuration with field type
 */
export interface TypedFieldConfig<TValue = any, TWhere = any, TFieldType extends FieldType = FieldType>
    extends Omit<FieldConfig<TValue, TWhere>, 'type' | 'customWhereBuilder' | 'operators'> {
    type: TFieldType;
    /**
     * Restrict operators to those allowed by the field type
     */
    operators?: OperatorsByFieldType<TFieldType>[];
    /**
     * Database field name for automatic where clause generation
     * When provided, customWhereBuilder becomes optional
     */
    dbField?: keyof TWhere;
    /**
     * Type-safe custom where builder that restricts operators based on field type
     * Optional when dbField is provided
     */
    customWhereBuilder?: TypedCustomWhereBuilder<TWhere, TValue, TFieldType>;
}

/**
 * Utility type: if a specific operators array is provided in options,
 * narrow the customWhereBuilder's operator parameter to those values.
 */
export type TypedFieldConfigWithOps<
    TValue = any,
    TWhere = any,
    TFieldType extends FieldType = FieldType,
    TOps extends readonly FilterOperator[] | undefined = undefined,
> = Omit<TypedFieldConfig<TValue, TWhere, TFieldType>, 'operators' | 'customWhereBuilder'> & {
    operators?: TOps extends readonly FilterOperator[]
        ? Readonly<TOps & ReadonlyArray<OperatorsByFieldType<TFieldType>>>
        : ReadonlyArray<OperatorsByFieldType<TFieldType>>;
    customWhereBuilder?: (
        operator: TOps extends readonly (infer Op)[]
            ? Extract<Op, OperatorsByFieldType<TFieldType>>
            : OperatorsByFieldType<TFieldType>,
        value: TValue,
        fieldName: string,
    ) => PrismaWhereClause<TWhere> | null;
};

/**
 * Specialized configuration for multiselect fields
 * Properly separates option value type from field value type
 */
export interface MultiselectFieldConfig<TOptionValue = any, TWhere = any>
    extends Omit<TypedFieldConfig<TOptionValue[], TWhere, 'multiselect'>, 'options'> {
    type: 'multiselect';
    /**
     * Options can be an array or async function returning array
     * Each option has a single value of type TOptionValue
     * The async function can optionally receive the current whereClause
     */
    options:
        | Array<{ label: string; value: TOptionValue }>
        | (() => Promise<Array<{ label: string; value: TOptionValue }>>)
        | ((whereClause?: TWhere) => Promise<Array<{ label: string; value: TOptionValue }>>);
}

/**
 * Specialized configuration for textSearch fields
 * Label is optional for textSearch fields
 */
export interface TextSearchFieldConfig<TWhere = any>
    extends Omit<TypedFieldConfig<string, TWhere, 'textSearch'>, 'label'> {
    type: 'textSearch';
    label?: string;
}

/**
 * Helper type to infer the where builder return type
 */
export type InferCustomWhereBuilder<TWhere, TValue> = CustomWhereBuilder<TWhere, TValue>;

/**
 * Complete filter configuration for an entity
 *
 * Combines multiple field configurations into a complete filter system.
 *
 * @template TWhere - The Prisma where input type for the entity
 * @property filters - Array of field configurations
 * @property defaultOperators - Optional overrides for default operators by field type
 *
 * @example
 * ```typescript
 * const userFilterConfig: FilterConfig<UserWhere> = {
 *   filters: [
 *     { id: 'name', label: 'Name', type: 'text' },
 *     { id: 'age', label: 'Age', type: 'number' },
 *     { id: 'status', label: 'Status', type: 'select', options: [...] }
 *   ],
 *   defaultOperators: {
 *     text: ['contains', 'startsWith'], // Override default text operators
 *   }
 * };
 * ```
 */
export interface FilterConfig<TWhere> {
    filters: (FieldConfig<any, TWhere> | TextSearchFieldConfig<TWhere>)[];
    defaultOperators?: Partial<Record<FieldType, FilterOperator[]>>;
}

/**
 * Filter builder state and methods
 *
 * The complete state object returned by useFilterBuilder hooks.
 * Contains current filters, methods to manipulate them, and the
 * generated Prisma where clause.
 *
 * @template TWhere - The Prisma where input type
 * @property filters - Current active filters
 * @property addFilter - Add a new filter by field ID
 * @property updateFilter - Update an existing filter
 * @property removeFilter - Remove a filter by ID
 * @property clearFilters - Remove all active filters
 * @property whereClause - Generated Prisma where clause (debounced)
 * @property isFilterEmpty - True if no filters are active
 *
 * @example
 * ```typescript
 * const filterState: FilterBuilderState<UserWhere> = useFilterBuilder(config);
 *
 * // Use in query
 * const users = await prisma.user.findMany({
 *   where: filterState.whereClause
 * });
 * ```
 */
export interface FilterBuilderState<TWhere> {
    filters: Filter[];
    addFilter: (filterId: string) => string | undefined;
    /** Replace the entire filter set in one shot (e.g. applying a saved view). */
    setFilters: (next: Filter[]) => void;
    updateFilter: (id: string, updates: Partial<Filter>) => void;
    removeFilter: (id: string) => void;
    clearFilters: () => void;
    whereClause: TWhere;
    isFilterEmpty: boolean;
    searchTerms: string[];
}

/**
 * Utility type to extract scalar fields from Prisma where input
 */
export type ScalarFields<T> = {
    [K in keyof T]: T[K] extends
        | { equals?: any }
        | { in?: any[] }
        | { contains?: any }
        | { gt?: any }
        | { lt?: any }
        | null
        | undefined
        ? K
        : never;
}[keyof T];

/**
 * Utility type to get the value type from a Prisma field condition
 */
export type ExtractFieldType<T> = T extends { equals?: infer V }
    ? V
    : T extends { in?: (infer V)[] }
      ? V
      : T extends { contains?: infer V }
        ? V
        : T extends { gt?: infer V }
          ? V
          : T extends { lt?: infer V }
            ? V
            : never;

/**
 * Map of field types to their default operators
 */
export const DEFAULT_OPERATORS: Record<FieldType, FilterOperator[]> = {
    text: ['equals', 'not', 'contains', 'startsWith', 'endsWith', 'isNull', 'isNotNull'],
    textSearch: ['equals', 'not', 'contains', 'startsWith', 'endsWith', 'isNull', 'isNotNull'],
    number: ['equals', 'not', 'gt', 'gte', 'lt', 'lte', 'between', 'isNull', 'isNotNull'],
    bigint: ['equals', 'not', 'gt', 'gte', 'lt', 'lte', 'between', 'isNull', 'isNotNull'],
    date: ['equals', 'not', 'gt', 'gte', 'lt', 'lte', 'between', 'withinRelative', 'isNull', 'isNotNull'],
    datetime: ['equals', 'not', 'gt', 'gte', 'lt', 'lte', 'between', 'withinRelative', 'isNull', 'isNotNull'],
    boolean: ['equals', 'not', 'isNull', 'isNotNull'],
    select: ['equals', 'not', 'in', 'notIn', 'isNull', 'isNotNull'],
    multiselect: ['in', 'notIn', 'isNull', 'isNotNull'],
    json: ['equals', 'not', 'contains', 'isNull', 'isNotNull'],
};

/**
 * Operator display labels.
 *
 * British sentence-case, phrased to read naturally after a field name in the
 * filter builder, e.g. "Claim owner is any of …", "Claim# contains …".
 * Date/datetime fields override the comparison operators in `getOperatorLabel`
 * ("is after" / "is on or after" etc.).
 */
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
    equals: 'is',
    not: 'is not',
    in: 'is any of',
    notIn: 'is none of',
    contains: 'contains',
    startsWith: 'starts with',
    endsWith: 'ends with',
    gt: 'greater than',
    gte: 'greater than or equal to',
    lt: 'less than',
    lte: 'less than or equal to',
    isNull: 'is empty',
    isNotNull: 'is not empty',
    between: 'is between',
    notBetween: 'is not between',
    withinRelative: 'is within',
};

/**
 * Operator icons/symbols
 */
export const OPERATOR_ICONS: Record<FilterOperator, string> = {
    equals: '=',
    not: '≠',
    in: '∈',
    notIn: '∉',
    contains: '∋',
    startsWith: '⊃',
    endsWith: '⊂',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    isNull: '∅',
    isNotNull: '✗',
    between: '↔',
    notBetween: '↮',
    withinRelative: '⊆',
};
